/**
 * BidPackage workspace — one coherent package workflow:
 * SCOPE → CANDIDATES → RFQ → RESPONSES → LEVELING → DECISION → EXECUTION.
 *
 * Everything about one package lives on this page: its scope and linked
 * requirements (with evidence), candidate providers, the quotes as leveled
 * by the backend, the normalized comparison, the EITL award decision, and
 * the execution linkage. The user is never sent across five unrelated
 * screens to answer "who should win and why".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import apiClient from "../services/api";
import {
  awardWorkflowApi,
  requirementsApi,
  rfqApi,
  classifyWorkflowError,
} from "../services/intelligenceService";
import { useAuth } from "../App";
import {
  canEditProcurement,
  canProposeAward,
  canViewCommercialInternals,
} from "../lib/roles";
import { hasCapability } from "../lib/capabilities";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  SectionLoading,
  SectionEmpty,
  SectionError,
  FeatureDisabled,
  NoAccess,
  PartialDataNotice,
  DisabledPill,
  UnknownValue,
} from "../components/workflow/WorkflowStates";
import {
  ProvenanceBadge,
  RequirementStatusBadge,
} from "../components/workflow/ProvenanceBadge";
import LevelingBoard from "../features/bid-packages/LevelingBoard";
import AwardDecisionPanel from "../features/bid-packages/AwardDecisionPanel";
import MandatoryRequirementsEditor from "../features/bid-packages/MandatoryRequirementsEditor";
import QuoteRecorder from "../features/bid-packages/QuoteRecorder";
import { fmtMoney } from "../features/bid-packages/levelingUtils";
import exchangeApi from "../services/exchangeService";
import { shortlistStateDisplay, readShortlistContract } from "../features/exchange/exchangePresentation";
import {
  ArrowLeft,
  Package,
  Users,
  Scale,
  Landmark,
  Truck,
  FileText,
  Send,
  Loader2,
  RefreshCw,
  ClipboardCheck,
  Building2,
  CheckCircle2,
  Network,
} from "lucide-react";

const STAGES = ["Scope", "Candidates", "RFQ", "Responses", "Leveling", "Decision", "Execution"];

const fmtDate = (value) => {
  if (!value) return null;
  try {
    return format(parseISO(value), "MMM d, yyyy");
  } catch {
    return String(value);
  }
};

const statusBadge = (status) => {
  const map = {
    upcoming: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    pending: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    submitted: "bg-violet-500/20 text-violet-400 border-violet-500/30",
    awarded: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    missed: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    cancelled: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  };
  return map[status] || "bg-muted text-muted-foreground border-border";
};

const SHORTLIST_BADGE_CLASS = {
  good: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  attention: "bg-amber-500/15 text-amber-400 border-amber-500/25",
  bad: "bg-red-500/15 text-red-400 border-red-500/25",
  unknown: "bg-muted text-muted-foreground border-border",
};

/**
 * A heading and its optional control cluster.
 *
 * flex-wrap is load-bearing, not cosmetic. Without it this row cannot break,
 * so the DECISION section's controls (two nowrap label+input pairs and a
 * button) forced the row to 637px inside a 412px device. Chromium then
 * expands the mobile layout VIEWPORT to the content width -- the page zooms
 * out to fit -- and in that state the award and authorization controls stop
 * being reliably actionable. Measured: document.scrollWidth 764 against a
 * 412 device width, seven of twelve governed-award browser cases unable to
 * click anything.
 *
 * min-w-0 on the heading lets a long title shrink instead of pushing; the
 * control cluster wraps beneath the title on a narrow screen rather than
 * being hidden, because every control in it names a commercial fact.
 */
const SectionHeading = ({ icon: Icon, title, right }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
    <h2 className="text-lg font-semibold font-heading flex items-center gap-2 min-w-0">
      <Icon className="w-5 h-5 text-primary shrink-0" />
      {title}
    </h2>
    {right}
  </div>
);

const BidPackageWorkspacePage = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { user, capabilities } = useAuth();

  const [pkg, setPkg] = useState(null);
  const [allBids, setAllBids] = useState([]);
  const [pkgError, setPkgError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [linkedReqs, setLinkedReqs] = useState(null);
  const [reqsClassified, setReqsClassified] = useState(null);

  const [procItems, setProcItems] = useState([]);

  const [proposals, setProposals] = useState([]);
  const [proposalsClassified, setProposalsClassified] = useState(null);
  const [activeProposal, setActiveProposal] = useState(null);

  const [levelResult, setLevelResult] = useState(null);
  const [levelClassified, setLevelClassified] = useState(null);
  const [levelRunning, setLevelRunning] = useState(false);
  const [proposeRunning, setProposeRunning] = useState(false);

  const [mandatoryReqs, setMandatoryReqs] = useState([]);
  const [requiredDurationDays, setRequiredDurationDays] = useState("");
  const [requiredOnSiteDate, setRequiredOnSiteDate] = useState("");

  // RFQ operating API surfaces
  const [quotes, setQuotes] = useState(null);
  const [quotesClassified, setQuotesClassified] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [candidatesClassified, setCandidatesClassified] = useState(null);
  // The Exchange shortlist for this same package, when the surface is
  // available — sourcing decisions made there must be visible here.
  const [shortlist, setShortlist] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [quoteDialog, setQuoteDialog] = useState({ open: false, revise: null });
  const [quoteActionBusy, setQuoteActionBusy] = useState(null);

  const loadQuotes = useCallback(async () => {
    try {
      const list = await rfqApi.listQuotes(packageId);
      setQuotes(list || []);
      setQuotesClassified(null);
    } catch (err) {
      setQuotes(null);
      setQuotesClassified(classifyWorkflowError(err));
    }
  }, [packageId]);

  const load = useCallback(async () => {
    setLoading(true);
    setPkgError(null);
    // Everything below is scoped to one package — reset it all so a package
    // switch can never show another package's commercial data.
    setLinkedReqs(null);
    setReqsClassified(null);
    setProcItems([]);
    setProposals([]);
    setProposalsClassified(null);
    setActiveProposal(null);
    setLevelResult(null);
    setLevelClassified(null);
    setMandatoryReqs([]);
    setRequiredDurationDays("");
    setRequiredOnSiteDate("");
    setQuotes(null);
    setQuotesClassified(null);
    setCandidates(null);
    setCandidatesClassified(null);
    setShortlist(null);
    try {
      const { data: bids } = await apiClient.get("/bids");
      setAllBids(bids || []);
      const found = (bids || []).find((b) => b.id === packageId);
      if (!found) {
        setPkgError({ kind: "not-found", message: "Bid package not found." });
        setLoading(false);
        return;
      }
      setPkg(found);

      // Secondary loads tolerate their own failures independently.
      if (found.project_id) {
        requirementsApi
          .listForProject(found.project_id)
          .then((data) =>
            setLinkedReqs(
              (data.requirements || []).filter((r) => r.bidPackageId === packageId)
            )
          )
          .catch((err) => setReqsClassified(classifyWorkflowError(err)));
        apiClient
          .get("/procurement", { params: { project_id: found.project_id } })
          .then(({ data }) =>
            setProcItems((data || []).filter((i) => i.bid_package_id === packageId))
          )
          .catch(() => setProcItems([]));
      }

      try {
        const list = await awardWorkflowApi.listProposals(packageId);
        setProposals(list || []);
        setProposalsClassified(null);
        if ((list || []).length > 0) {
          const detail = await awardWorkflowApi.getProposal(packageId, list[0].proposalId);
          setActiveProposal(detail);
        } else {
          setActiveProposal(null);
        }
      } catch (err) {
        setProposalsClassified(classifyWorkflowError(err));
      }

      // RFQ operating API: durable quotes + candidate providers. Each states
      // its own availability; a 503 means the flag is off, not emptiness.
      await loadQuotes();
      try {
        const cands = await rfqApi.candidates(packageId);
        setCandidates(cands);
        setCandidatesClassified(null);
      } catch (err) {
        setCandidatesClassified(classifyWorkflowError(err));
      }

      /**
       * The Exchange shortlist for this same package. Sourcing decisions are
       * made over there; showing them here is what stops a PM inviting a
       * provider their own team already excluded, or re-entering one that is
       * already on the list. Entirely optional — the Exchange has its own
       * entitlement, and its absence changes nothing on this page.
       */
      if (found.project_id) {
        try {
          const list = await exchangeApi.getShortlist(found.project_id, packageId);
          setShortlist(list);
        } catch {
          setShortlist(null);
        }
      }
      apiClient
        .get("/vendors")
        .then(({ data }) => setVendors(data || []))
        .catch(() => setVendors([]));
    } catch (err) {
      setPkgError(classifyWorkflowError(err));
    } finally {
      setLoading(false);
    }
  }, [packageId, loadQuotes]);

  useEffect(() => {
    load();
  }, [load]);

  const runLeveling = async () => {
    setLevelRunning(true);
    setLevelClassified(null);
    try {
      const result = await awardWorkflowApi.level(packageId, {
        requirements: mandatoryReqs,
      });
      setLevelResult(result);
      toast.success(`Leveled ${result.quoteCount} quote${result.quoteCount === 1 ? "" : "s"}.`);
    } catch (err) {
      const c = classifyWorkflowError(err);
      setLevelClassified(c);
      if (c.kind !== "disabled" && c.kind !== "conflict") toast.error(c.message);
    } finally {
      setLevelRunning(false);
    }
  };

  const runPropose = async () => {
    setProposeRunning(true);
    try {
      const body = { requirements: mandatoryReqs };
      if (requiredDurationDays !== "") body.requiredDurationDays = Number(requiredDurationDays);
      if (requiredOnSiteDate) body.requiredOnSiteDate = requiredOnSiteDate;
      const detail = await awardWorkflowApi.propose(packageId, body);
      setActiveProposal(detail);
      toast.success("Recommendation calculated. It awaits your EITL decision — nothing is awarded.");
      const list = await awardWorkflowApi.listProposals(packageId).catch(() => null);
      if (list) setProposals(list);
    } catch (err) {
      const c = classifyWorkflowError(err);
      toast.error(c.kind === "conflict" ? c.message : c.message);
    } finally {
      setProposeRunning(false);
    }
  };

  const quoteAction = async (quote, verb) => {
    setQuoteActionBusy(quote.quoteId);
    try {
      if (verb === "submit") {
        await rfqApi.submitQuote(quote.quoteId);
        toast.success("Quote submitted.");
      } else if (verb === "withdraw") {
        const reason = window.prompt("Reason for withdrawal (kept on the record):") || undefined;
        await rfqApi.withdrawQuote(quote.quoteId, { reason });
        toast.success("Quote withdrawn — it stays on record and is disqualified from leveling.");
      } else if (verb === "clarify") {
        const question = window.prompt("What clarification is needed from this bidder?");
        if (!question) return;
        await rfqApi.requestClarification(quote.quoteId, question);
        toast.success("Clarification requested.");
      }
      await loadQuotes();
    } catch (err) {
      toast.error(classifyWorkflowError(err).message);
    } finally {
      setQuoteActionBusy(null);
    }
  };

  const openRevise = async (quote) => {
    try {
      const detail = await rfqApi.getQuote(quote.quoteId);
      setQuoteDialog({ open: true, revise: detail });
    } catch (err) {
      toast.error(classifyWorkflowError(err).message);
    }
  };

  /**
   * Shortlist membership keyed by the same (providerType, providerId) pair
   * the RFQ candidate list uses. The vocabularies line up exactly for vendor
   * and subcontractor_company; an entry keyed on a marketplace listing or an
   * organization has no counterpart here and is deliberately not guessed at.
   */
  /**
   * The shortlist payload, decided before anything reads it.
   *
   * `(shortlist?.entries || []).forEach(...)` used to sit here, and it
   * white-screened the whole workspace on an array payload: `.entries` is
   * `Array.prototype.entries`, a truthy FUNCTION, so the `|| []` never fired.
   * Optional chaining defends one field; it does not validate a shape.
   */
  const shortlistContract = useMemo(() => readShortlistContract(shortlist), [shortlist]);

  const shortlistByProvider = useMemo(() => {
    const map = {};
    shortlistContract.entries.forEach((entry) => {
      if (entry?.recordType === "vendor" || entry?.recordType === "subcontractor_company") {
        map[`${entry.recordType}:${entry.recordId}`] = entry.state;
      }
    });
    return map;
  }, [shortlistContract]);

  const competing = useMemo(
    () =>
      allBids.filter(
        (b) =>
          b.id !== packageId &&
          b.project_id === pkg?.project_id &&
          (b.trade || "") === (pkg?.trade || "")
      ),
    [allBids, pkg, packageId]
  );

  // Prefer the persisted evaluation on the active proposal; fall back to the
  // ad-hoc leveling run.
  const leveling = activeProposal?.leveling?.quotes ? activeProposal.leveling : levelResult?.leveling;

  const currentStage = useMemo(() => {
    if (
      pkg?.status === "awarded" ||
      proposals.some((p) => ["APPROVED", "AUTHORIZED_TO_PROCEED"].includes(p.state))
    )
      return 6;
    if (proposals.some((p) => p.state === "PROPOSED")) return 5;
    if (leveling?.quotes?.length) return 4;
    if ((quotes || []).length > 0) return 3;
    if (levelClassified?.kind === "conflict") return 2; // RFQ out, no responses yet
    if (["pending", "submitted"].includes(pkg?.status)) return 2;
    if (pkg?.status === "upcoming") return 1;
    return 0;
  }, [pkg, proposals, leveling, levelClassified, quotes]);

  if (loading) return <SectionLoading label="Loading bid package…" />;
  if (pkgError)
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/procurement")}>
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Procurement
        </Button>
        {pkgError.kind === "not-found" ? (
          <SectionEmpty
            icon={Package}
            title="Bid package not found"
            hint="It may have been removed, or it belongs to a different tenant."
          />
        ) : (
          <SectionError error={pkgError} onRetry={load} />
        )}
      </div>
    );

  const commercialAllowed = canViewCommercialInternals(user);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2 h-7 text-xs" onClick={() => navigate("/procurement")}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1" />
            Procurement
          </Button>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" />
            {pkg.title}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {pkg.trade && <span>{pkg.trade}</span>}
            {pkg.project_name && (
              <>
                <span>·</span>
                <Link to={`/projects/${pkg.project_id}`} className="text-primary hover:underline flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5" />
                  {pkg.project_name}
                </Link>
              </>
            )}
            {pkg.due_date && <span>· Due {fmtDate(pkg.due_date)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={statusBadge(pkg.status)}>
            {pkg.status}
          </Badge>
          {pkg.estimated_value ? (
            <Badge variant="outline" className="bg-muted/50 border-border font-normal">
              Est. {fmtMoney(pkg.estimated_value)}
            </Badge>
          ) : null}
        </div>
      </div>

      {/* Stage rail */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {STAGES.map((stage, i) => (
          <div key={stage} className="flex items-center gap-1 shrink-0">
            <div
              className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                i < currentStage
                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"
                  : i === currentStage
                    ? "bg-primary/20 text-primary border-primary/40"
                    : "bg-muted/30 text-muted-foreground border-border"
              }`}
            >
              {i < currentStage && <CheckCircle2 className="w-3 h-3 inline mr-1 -mt-0.5" />}
              {stage.toUpperCase()}
            </div>
            {i < STAGES.length - 1 && <div className="w-3 h-px bg-border" />}
          </div>
        ))}
      </div>

      {/* SCOPE */}
      <section>
        <SectionHeading icon={FileText} title="Scope & Requirements" />
        <Card className="border-border">
          <CardContent className="pt-4 space-y-3">
            {pkg.description ? (
              <p className="text-sm text-muted-foreground">{pkg.description}</p>
            ) : (
              <p className="text-xs text-muted-foreground">No scope description recorded on the package.</p>
            )}
            {reqsClassified?.kind === "disabled" ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <DisabledPill label="Requirements register not enabled" />
                <span>Linked requirements cannot be shown.</span>
              </div>
            ) : linkedReqs === null ? (
              pkg.project_id ? (
                <SectionLoading label="Loading linked requirements…" />
              ) : (
                <p className="text-xs text-muted-foreground">
                  This package is not linked to a project, so no register requirements can be attached.
                </p>
              )
            ) : linkedReqs.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No register requirements are linked to this package yet. Link them from the
                project's Requirements tab to drive evaluation from evidence.
              </p>
            ) : (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <ClipboardCheck className="w-3.5 h-3.5" />
                  Linked requirements ({linkedReqs.length})
                </p>
                {linkedReqs.map((r) => (
                  <div key={r.requirementId} className="flex items-center justify-between gap-2 rounded-lg bg-muted/30 px-3 py-2">
                    <span className="text-sm truncate">{r.title}</span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      <ProvenanceBadge verificationStatus={r.verificationStatus} />
                      <RequirementStatusBadge status={r.status} />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* CANDIDATES */}
      <section>
        {/* MALFORMED IS NOT EMPTY.
            A shortlist payload we cannot read must never be rendered as
            "no shortlist" -- that is a commercial claim about who was
            considered, and we would be inventing it. It says the section is
            unreadable, in the existing partial-data vocabulary, and the
            chip above stays away rather than showing a fabricated count. */}
        {shortlistContract.status === 'malformed' && (
          <div className="mb-3" data-testid="shortlist-contract-mismatch">
            <PartialDataNotice
              unavailable={[{ name: "This package's Exchange shortlist", reason: 'error' }]}
            />
          </div>
        )}
        <SectionHeading
          icon={Users}
          title="Candidates"
          right={
            commercialAllowed &&
            pkg.project_id && (
              <div className="flex flex-wrap items-center gap-2">
                {/* A shortlist already exists for this package — send the PM
                    to it rather than to a provider search they have already
                    done. */}
                {shortlistContract.status === 'ok' && shortlistContract.count > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate(
                        `/exchange/build?project_id=${pkg.project_id}&package_id=${packageId}&tab=shortlist`
                      )
                    }
                    title="Open this package's shortlist in the Exchange"
                  >
                    <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
                    Shortlist ({shortlistContract.count})
                    {(shortlistContract.stateFacets.PROPOSED || 0) > 0 && (
                      <span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500/25 px-1 text-[9px] font-bold text-amber-300">
                        {shortlistContract.stateFacets.PROPOSED}
                      </span>
                    )}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(
                      `/exchange/build?project_id=${pkg.project_id}&package_id=${packageId}`
                    )
                  }
                  title="Find eligible providers for this package in the Exchange"
                >
                  <Network className="w-3.5 h-3.5 mr-1.5" />
                  Find providers
                </Button>
              </div>
            )
          }
        />
        <Card className="border-border">
          <CardContent className="pt-4 space-y-3">
            {candidates ? (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">
                  Candidate providers for {candidates.trade || pkg.trade || "this trade"} (
                  {(candidates.candidates || []).length})
                </p>
                {(candidates.candidates || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No vendors, subcontractor companies, or marketplace interests match this
                    package yet.
                  </p>
                ) : (
                  (candidates.candidates || []).map((c) => (
                    <div
                      key={`${c.providerType}-${c.providerId}`}
                      className="flex items-center justify-between gap-2 rounded-lg bg-muted/30 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {c.providerType.replaceAll("_", " ")}
                          {c.trade ? ` · ${c.trade}` : ""}
                          {c.contactEmail ? ` · ${c.contactEmail}` : ""}
                        </p>
                      </div>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {/* A sourcing decision already made in the Exchange.
                            Without it the same provider gets judged twice —
                            or invited after being deliberately excluded. */}
                        {shortlistByProvider[`${c.providerType}:${c.providerId}`] && (
                          <Badge
                            variant="outline"
                            className={`${
                              SHORTLIST_BADGE_CLASS[
                                shortlistStateDisplay(
                                  shortlistByProvider[`${c.providerType}:${c.providerId}`]
                                ).tone
                              ] || SHORTLIST_BADGE_CLASS.unknown
                            } font-normal text-[10px]`}
                            title="Recorded on this package's Exchange shortlist."
                          >
                            {
                              shortlistStateDisplay(
                                shortlistByProvider[`${c.providerType}:${c.providerId}`]
                              ).label
                            }
                          </Badge>
                        )}
                        {c.tradeMatch === true && (
                          <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400 border-emerald-500/25 font-normal text-[10px]">
                            Trade match
                          </Badge>
                        )}
                        {c.status && (
                          <Badge variant="outline" className="bg-muted/50 border-border font-normal text-[10px]">
                            {c.status}
                          </Badge>
                        )}
                      </span>
                    </div>
                  ))
                )}
                {(candidates.missingInformation || []).map((m, i) => (
                  <p key={i} className="text-xs text-amber-300/90">{m}</p>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg bg-muted/30 p-3">
                  <p className="text-xs text-muted-foreground">Package bidder</p>
                  <p className="text-sm font-medium">
                    {pkg.vendor_name || <UnknownValue reason="No vendor attached to this package." />}
                  </p>
                  {pkg.source && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">Source: {pkg.source}</p>
                  )}
                  {candidatesClassified?.kind === "disabled" && (
                    <div className="mt-2">
                      <DisabledPill label="Candidate view not enabled (RFQ API off)" />
                    </div>
                  )}
                </div>
                {commercialAllowed && (
                  <div className="rounded-lg bg-muted/30 p-3">
                    <p className="text-xs text-muted-foreground">
                      Competing packages — same project & trade
                    </p>
                    {competing.length === 0 ? (
                      <p className="text-sm text-muted-foreground">None recorded</p>
                    ) : (
                      competing.map((b) => (
                        <Link
                          key={b.id}
                          to={`/procurement/packages/${b.id}`}
                          className="block text-sm text-primary hover:underline"
                        >
                          {b.vendor_name || b.title} {b.estimated_value ? `· ${fmtMoney(b.estimated_value)}` : ""}
                        </Link>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* RESPONSES — durable quotes via the RFQ operating API */}
      <section>
        <SectionHeading
          icon={FileText}
          title="Responses (Quotes)"
          right={
            commercialAllowed &&
            hasCapability(capabilities, "rfq.create") &&
            quotesClassified?.kind !== "disabled" && (
              <Button size="sm" onClick={() => setQuoteDialog({ open: true, revise: null })}>
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Record quote
              </Button>
            )
          }
        />
        {!commercialAllowed ? (
          <NoAccess message="Quote responses are internal commercial records." />
        ) : quotesClassified?.kind === "disabled" ? (
          <FeatureDisabled
            feature="The RFQ quote lifecycle"
            hint="Recording, submitting, and revising quotes is built but OMNAS_RFQ_API_ENABLED is off for this environment. Leveling still works against quotes ingested by other means."
          />
        ) : quotesClassified ? (
          <SectionError error={quotesClassified} onRetry={loadQuotes} label="Quotes failed to load" />
        ) : quotes === null ? (
          <SectionLoading label="Loading quotes…" />
        ) : quotes.length === 0 ? (
          <SectionEmpty
            icon={FileText}
            title="No quotes recorded yet"
            hint={
              hasCapability(capabilities, "rfq.create")
                ? "Record each bidder's response as it arrives. Revisions are append-only — a revised quote never overwrites what the bidder originally submitted."
                : "Quotes appear here as the team records bidder responses."
            }
          />
        ) : (
          <Card className="border-border">
            <CardContent className="p-0 divide-y divide-border">
              {quotes.map((q) => (
                <div key={q.quoteId} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {q.bidderDisplayName}
                      <span className="text-xs text-muted-foreground font-normal">
                        {" "}· rev {q.revision}
                        {q.isCurrent ? " (current)" : " (superseded)"}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {fmtMoney(q.baseAmount) || "No amount"}
                      {q.proposedDurationDays != null ? ` · ${q.proposedDurationDays} days` : ""}
                      {q.leadTimeDays != null ? ` · lead ${q.leadTimeDays}d` : ""}
                      {q.validUntil ? ` · valid until ${fmtDate(q.validUntil)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="bg-muted/50 border-border font-normal">
                      {q.status.replaceAll("_", " ")}
                    </Badge>
                    {hasCapability(capabilities, "rfq.create") && q.isCurrent && (
                      <>
                        {q.status === "DRAFT" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={quoteActionBusy === q.quoteId} onClick={() => quoteAction(q, "submit")}>
                            Submit
                          </Button>
                        )}
                        {!["WITHDRAWN", "DISQUALIFIED", "NOT_SELECTED", "SELECTED", "EXPIRED"].includes(q.status) && (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={quoteActionBusy === q.quoteId} onClick={() => openRevise(q)}>
                              Revise
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={quoteActionBusy === q.quoteId} onClick={() => quoteAction(q, "clarify")}>
                              Clarify
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" disabled={quoteActionBusy === q.quoteId} onClick={() => quoteAction(q, "withdraw")}>
                              Withdraw
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </section>

      {/* LEVELING */}
      <section>
        <SectionHeading
          icon={Scale}
          title="Leveling"
          right={
            commercialAllowed &&
            canEditProcurement(user) && (
              <Button size="sm" variant="outline" onClick={runLeveling} disabled={levelRunning}>
                {levelRunning ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                )}
                {leveling ? "Re-run leveling" : "Run leveling"}
              </Button>
            )
          }
        />
        {!commercialAllowed ? (
          <NoAccess message="Commercial comparison and competing bidder information are internal. Your role sees only your own submissions." />
        ) : (
          <div className="space-y-4">
            {canEditProcurement(user) && (
              <Card className="border-border">
                <CardContent className="pt-4">
                  <MandatoryRequirementsEditor
                    value={mandatoryReqs}
                    onChange={setMandatoryReqs}
                    linkedRequirements={linkedReqs || []}
                  />
                </CardContent>
              </Card>
            )}

            {leveling?.quotes?.length ? (
              <LevelingBoard
                leveling={leveling}
                recommendedQuoteId={activeProposal?.recommendation?.recommendedQuoteId}
              />
            ) : levelClassified?.kind === "disabled" ? (
              <FeatureDisabled
                feature="Quote leveling"
                hint="The leveling engine is built but OMNAS_QUOTE_LEVELING_ENABLED is off for this environment."
              />
            ) : levelClassified?.kind === "conflict" ? (
              <SectionEmpty
                icon={Scale}
                title="No quotes to level yet"
                hint="Record bidder responses in the Responses section above; leveling then compares them like-for-like."
              />
            ) : levelClassified ? (
              <SectionError error={levelClassified} onRetry={runLeveling} label="Leveling failed" />
            ) : (
              <SectionEmpty
                icon={Scale}
                title="Not leveled yet"
                hint="Run leveling to compare the package's quotes like-for-like: lowest submitted, lowest normalized, lowest complete, and lowest responsive & qualified — with the reasons they differ."
              />
            )}
          </div>
        )}
      </section>

      {/* DECISION */}
      <section>
        <SectionHeading
          icon={Landmark}
          title="Award Decision (EITL)"
          right={
            commercialAllowed &&
            canProposeAward(user) && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <Label className="text-[11px] text-muted-foreground whitespace-nowrap">Required duration (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={requiredDurationDays}
                    onChange={(e) => setRequiredDurationDays(e.target.value)}
                    className="h-8 w-20 text-xs"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-[11px] text-muted-foreground whitespace-nowrap">Required on-site</Label>
                  <Input
                    type="date"
                    value={requiredOnSiteDate}
                    onChange={(e) => setRequiredOnSiteDate(e.target.value)}
                    className="h-8 w-36 text-xs"
                  />
                </div>
                <Button size="sm" onClick={runPropose} disabled={proposeRunning}>
                  {proposeRunning ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {activeProposal ? "Re-run recommendation" : "Generate recommendation"}
                </Button>
              </div>
            )
          }
        />
        {!commercialAllowed ? (
          <NoAccess message="Award evaluation is internal." />
        ) : proposalsClassified?.kind === "disabled" ? (
          <FeatureDisabled
            feature="The award workflow"
            hint="Leveling, recommendation, and the EITL gate are built but switched off (OMNAS_AWARD_WORKFLOW_ENABLED and related flags)."
          />
        ) : proposalsClassified ? (
          <SectionError
            error={proposalsClassified}
            onRetry={load}
            label="Award proposals failed to load"
          />
        ) : activeProposal ? (
          <AwardDecisionPanel
            packageId={packageId}
            proposal={activeProposal}
            history={proposals}
            user={user}
            onDecided={load}
          />
        ) : (
          <SectionEmpty
            icon={Landmark}
            title="No award recommendation yet"
            hint={
              canProposeAward(user)
                ? "Generate a recommendation once quotes are leveled. It will wait for an explicit human (EITL) decision — the system never awards on its own."
                : "A project manager, superintendent, or admin generates the recommendation; the EITL decision then appears here."
            }
          />
        )}
      </section>

      {/* EXECUTION */}
      <section>
        <SectionHeading icon={Truck} title="Execution Linkage" />
        <Card className="border-border">
          <CardContent className="pt-4 space-y-2">
            {procItems.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No procurement items are linked to this package. An approved decision does not
                create one automatically — issuing the subcontract or purchase order is a
                separate, deliberate step that is not enabled in this environment.
              </p>
            ) : (
              procItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between gap-2 rounded-lg bg-muted/30 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.status}
                      {item.expected_delivery ? ` · expected ${fmtDate(item.expected_delivery)}` : ""}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs shrink-0"
                    onClick={() => navigate(`/procurement?project_id=${pkg.project_id || ""}`)}
                  >
                    Open in procurement
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </section>

      <QuoteRecorder
        open={quoteDialog.open}
        onOpenChange={(open) => setQuoteDialog((d) => ({ ...d, open }))}
        packageId={packageId}
        vendors={vendors}
        reviseQuote={quoteDialog.revise}
        onSaved={loadQuotes}
      />
    </div>
  );
};

export default BidPackageWorkspacePage;
